import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Layers, QrCode, Filter, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import Table, { ActionButton, ActionMenu } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const PointsPage = () => {
  const { user } = useAuth();
  const [points, setPoints] = useState([]);
  const [farms, setFarms] = useState([]);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [plotsLoading, setPlotsLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const [selectedFarm, setSelectedFarm] = useState('');
  const [selectedPlot, setSelectedPlot] = useState('');

  const [filters, setFilters] = useState({
    crop: '',
    seed: '',
    source: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedPoint, setSelectedBed] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // QR Code generation state
  const [qrCodeGenerating, setQrCodeGenerating] = useState({});

  // Report modal data
  const [reportBedData, setReportBedData] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    site_id: '',
    point_id: '',
    parameter: '',
    latitude: '',
    longitude: '',
    description: '',
    // Sensor Metadata
    sensor_id: '',
    sensor_type: '',
    measurement_units: '',
    calibration_date: '',
    data_frequency: '',
    power_source: '',
    communication_type: '',
  });

  const columns = [
    {
      key: 'point_id',
      title: 'Point ID',
      sortable: true,
      width: '120px',
      render: (point) => (
        <div className="font-medium text-secondary-900">
          {point.point_id || 'N/A'}
        </div>
      ),
    },
    {
      key: 'parameter',
      title: 'Parameter',
      sortable: true,
      render: (point) => (
        <div className="flex items-center">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <Layers className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="font-medium text-secondary-900">{point.parameter || 'Unknown Parameter'}</div>
            <div className="text-sm text-secondary-500">{point.measurement_units || 'N/A'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'sensor_type',
      title: 'Sensor Type',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (point) => (
        <div>
          <div className="font-medium text-secondary-900">{point.sensor_type || 'Not specified'}</div>
          <div className="text-sm text-secondary-500">{point.sensor_id || 'No ID'}</div>
        </div>
      ),
    },
    {
      key: 'calibration_date',
      title: 'Last Calibration',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (point) => point.calibration_date ? new Date(point.calibration_date).toLocaleDateString() : 'Not set',
    },
    {
      key: 'qr_code',
      title: 'QR Code',
      className: 'hidden lg:table-cell',
      render: (point) => (
        point.qr_code ? (
          <div className="flex items-center">
            <QrCode className="w-4 h-4 text-green-600 mr-1" />
            <a
              href={point.qr_code}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              View QR
            </a>
          </div>
        ) : (
          <button
            onClick={() => handleGenerateQRCode(point.id)}
            disabled={qrCodeGenerating[point.id]}
            className="btn btn-sm btn-secondary text-xs px-2 py-1 disabled:opacity-50"
          >
            {qrCodeGenerating[point.id] ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></div>
                Generating...
              </>
            ) : (
              <>
                <QrCode className="w-3 h-3 mr-1" />
                Generate QR
              </>
            )}
          </button>
        )
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (point) => new Date(point.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (point) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewBed(point)}
          />
          <ActionMenu
            item={point}
            actions={[
              {
                key: 'report',
                label: 'Submit Report',
                icon: AlertTriangle,
                variant: 'warning',
                onClick: handleReportBed,
              },
              {
                key: 'edit',
                label: 'Edit Point',
                icon: Edit,
                onClick: handleEditBed,
              },
              {
                key: 'delete',
                label: 'Delete Point',
                icon: Trash2,
                variant: 'danger',
                onClick: handleDeleteBed,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadFarms();
  }, []);

  useEffect(() => {
    if (selectedFarm) {
      loadPlots();
      setSelectedPlot('');
      setPoints([]);
    } else {
      setPlots([]);
      setSelectedPlot('');
      setPoints([]);
    }
  }, [selectedFarm]);

  useEffect(() => {
    if (selectedPlot) {
      loadBeds();
    } else {
      setPoints([]);
    }
  }, [selectedPlot, filters, sortConfig]);

  const loadFarms = async () => {
    try {
      setFarmsLoading(true);
      const response = await apiService.getWatersheds();

      if (response.success) {
        const farmsData = response.data.watersheds || [];
        setFarms(farmsData);

        // Auto-select first farm if available and no farm is currently selected
        if (farmsData.length > 0 && !selectedFarm) {
          setSelectedFarm(farmsData[0].id);
        }
      } else {
        toast.error(response.message || 'Failed to load farms');
      }
    } catch (error) {
      console.error('Error loading farms:', error);
      toast.error('Failed to load farms');
    } finally {
      setFarmsLoading(false);
    }
  };

  const loadPlots = async () => {
    if (!selectedFarm) return;

    try {
      setPlotsLoading(true);
      const response = await apiService.getSites({ farm_id: selectedFarm });

      if (response.success) {
        const plotsData = response.data.sites || [];
        setPlots(plotsData);

        // Auto-select first plot if available and no plot is currently selected
        if (plotsData.length > 0 && !selectedPlot) {
          setSelectedPlot(plotsData[0].id);
        }
      } else {
        toast.error(response.message || 'Failed to load plots');
      }
    } catch (error) {
      console.error('Error loading plots:', error);
      toast.error('Failed to load plots');
    } finally {
      setPlotsLoading(false);
    }
  };

  const loadBeds = async () => {
    if (!selectedPlot) return;

    try {
      setLoading(true);
      const params = {
        plot_id: selectedPlot,
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getPoints(params);

      if (response.success) {
        setPoints(response.data.points || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load points');
      }
    } catch (error) {
      console.error('Error loading points:', error);
      toast.error('Failed to load points');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewBed = (point) => {
    setSelectedBed(point);
    setShowViewModal(true);
  };

  const handleEditBed = (point) => {
    setSelectedBed(point);
    setFormData({
      site_id: point.site_id || '',
      point_id: point.point_id || '',
      parameter: point.parameter || '',
      latitude: point.latitude ? point.latitude.toString() : '',
      longitude: point.longitude ? point.longitude.toString() : '',
      description: point.description || '',
      // Sensor Metadata
      sensor_id: point.sensor_id || '',
      sensor_type: point.sensor_type || '',
      measurement_units: point.measurement_units || '',
      calibration_date: point.calibration_date ? point.calibration_date.split('T')[0] : '',
      data_frequency: point.data_frequency || '',
      power_source: point.power_source || '',
      communication_type: point.communication_type || '',
    });
    setShowEditModal(true);
  };

  const handleDeleteBed = (point) => {
    setSelectedBed(point);
    setShowDeleteModal(true);
  };

  const handleReportBed = async (point) => {
    try {
      // Get the full point data
      const pointResponse = await apiService.getPoint(point.id);
      if (!pointResponse.success) {
        toast.error('Failed to load point details');
        return;
      }

      // Get the watershed data
      const watershedResponse = await apiService.getWatershed(selectedFarm);
      if (!watershedResponse.success) {
        toast.error('Failed to load watershed details');
        return;
      }

      // Get the site data
      const siteResponse = await apiService.getSite(point.site_id || selectedPlot);
      if (!siteResponse.success) {
        toast.error('Failed to load site details');
        return;
      }

      // Structure the data as expected by ReportForm (river monitoring structure)
      const pointData = {
        point: pointResponse.data,
        watershed: watershedResponse.data,
        site: siteResponse.data
      };

      setReportBedData(pointData);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error loading point data for report:', error);
      toast.error('Failed to load point details for reporting');
    }
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    setReportBedData(null);
    toast.success('Report submitted successfully');
  };

  const handleCreateBed = () => {
    if (!selectedPlot) {
      toast.error('Please select a site first');
      return;
    }

    setSelectedBed(null);
    setFormData({
      site_id: selectedPlot,
      point_id: '',
      parameter: '',
      latitude: '',
      longitude: '',
      description: '',
      // Sensor Metadata
      sensor_id: '',
      sensor_type: '',
      measurement_units: '',
      calibration_date: '',
      data_frequency: '',
      power_source: '',
      communication_type: '',
    });
    setShowCreateModal(true);
  };

  const handleFormSubmit = async () => {
    let processedFormData = null;

    try {
      setFormLoading(true);

      // Validate required fields
      if (!formData.point_id || !formData.parameter || !formData.site_id) {
        toast.error('Please fill in Point ID, Parameter, and Site');
        setFormLoading(false);
        return;
      }

      // Process calibration date
      let calibrationDate = null;
      if (formData.calibration_date) {
        const dateObj = new Date(formData.calibration_date);
        if (!isNaN(dateObj.getTime())) {
          calibrationDate = dateObj.toISOString();
        }
      }

      processedFormData = {
        site_id: formData.site_id,
        point_id: formData.point_id.trim(),
        parameter: formData.parameter.trim(),
        description: formData.description?.trim() || '',
        coordinates: {
          latitude: parseFloat(formData.latitude) || 0,
          longitude: parseFloat(formData.longitude) || 0,
        },
        // Sensor Metadata
        sensor_id: formData.sensor_id?.trim() || '',
        sensor_type: formData.sensor_type?.trim() || '',
        measurement_units: formData.measurement_units?.trim() || '',
        calibration_date: calibrationDate,
        data_frequency: formData.data_frequency?.trim() || '',
        power_source: formData.power_source?.trim() || '',
        communication_type: formData.communication_type?.trim() || '',
      };

      let response;
      if (selectedPoint) {
        response = await apiService.updatePoint(selectedPoint.id, processedFormData);
      } else {
        response = await apiService.createPoint(processedFormData);
      }

      if (response.success) {
        toast.success(selectedPoint ? 'Point updated successfully' : 'Point created successfully');
        setShowEditModal(false);
        setShowCreateModal(false);
        loadBeds(); // Load points
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error('Form data being sent:', processedFormData);

      // Try to extract more specific error message
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Operation failed. Please check console for details.';
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPoint) return;

    try {
      const response = await apiService.deletePoint(selectedPoint.id);

      if (response.success) {
        toast.success('Point deleted successfully');
        setShowDeleteModal(false);
        loadBeds();
      } else {
        toast.error(response.message || 'Failed to delete point');
      }
    } catch (error) {
      console.error('Error deleting point:', error);
      toast.error('Failed to delete point');
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'size') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    if (field === 'bed_number') {
      if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateQRCode = async (bedId) => {
    try {
      setQrCodeGenerating(prev => ({ ...prev, [bedId]: true }));

      const response = await apiService.generatePointQRCode(bedId);

      if (response.success) {
        toast.success('QR code generated successfully');
        loadBeds(); // Reload to get the updated point with QR code
      } else {
        toast.error(response.message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR code generation error:', error);
      toast.error(error.message || 'Failed to generate QR code');
    } finally {
      setQrCodeGenerating(prev => ({ ...prev, [bedId]: false }));
    }
  };

  const renderFormFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Point ID *
        </label>
        <input
          type="text"
          value={formData.point_id}
          onChange={(e) => handleInputChange('point_id', e.target.value)}
          className="form-input"
          placeholder="Enter point ID (e.g., P001, SITE-A-01)"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Parameter *
        </label>
        <input
          type="text"
          value={formData.parameter}
          onChange={(e) => handleInputChange('parameter', e.target.value)}
          className="form-input"
          placeholder="e.g., pH, DO, EC, Turbidity"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Latitude *
          </label>
          <input
            type="number"
            step="any"
            value={formData.latitude}
            onChange={(e) => handleInputChange('latitude', e.target.value)}
            className="form-input"
            placeholder="Enter latitude"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Longitude *
          </label>
          <input
            type="number"
            step="any"
            value={formData.longitude}
            onChange={(e) => handleInputChange('longitude', e.target.value)}
            className="form-input"
            placeholder="Enter longitude"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={3}
          className="form-input"
          placeholder="Enter point description"
        />
      </div>

      <div className="border-t pt-4">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">Sensor Metadata</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Sensor ID
            </label>
            <input
              type="text"
              value={formData.sensor_id}
              onChange={(e) => handleInputChange('sensor_id', e.target.value)}
              className="form-input"
              placeholder="Enter sensor ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Sensor Type
            </label>
            <input
              type="text"
              value={formData.sensor_type}
              onChange={(e) => handleInputChange('sensor_type', e.target.value)}
              className="form-input"
              placeholder="e.g., pH sensor, DO meter"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Measurement Units
            </label>
            <input
              type="text"
              value={formData.measurement_units}
              onChange={(e) => handleInputChange('measurement_units', e.target.value)}
              className="form-input"
              placeholder="e.g., mg/L, μS/cm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Calibration Date
            </label>
            <input
              type="date"
              value={formData.calibration_date}
              onChange={(e) => handleInputChange('calibration_date', e.target.value)}
              className="form-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Data Frequency
            </label>
            <input
              type="text"
              value={formData.data_frequency}
              onChange={(e) => handleInputChange('data_frequency', e.target.value)}
              className="form-input"
              placeholder="e.g., Every 15 minutes, Hourly"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Power Source
            </label>
            <input
              type="text"
              value={formData.power_source}
              onChange={(e) => handleInputChange('power_source', e.target.value)}
              className="form-input"
              placeholder="e.g., Solar, Battery, AC"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Communication Type
            </label>
            <input
              type="text"
              value={formData.communication_type}
              onChange={(e) => handleInputChange('communication_type', e.target.value)}
              className="form-input"
              placeholder="e.g., WiFi, LoRa, 4G"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Monitoring Points</h1>
          <p className="text-sm md:text-base text-secondary-600">Manage water quality monitoring points and sensor data</p>
        </div>
        {selectedPlot && user && ['admin', 'super_admin', 'watershed_manager'].includes(user.role) && (
          <button className="btn btn-primary w-full md:w-auto" onClick={handleCreateBed}>
            <Plus className="w-4 h-4 mr-2" />
            <span className="md:inline">Add Point</span>
          </button>
        )}
      </div>

      {/* Watershed and Site Selection */}
      <div className="bg-white rounded-lg shadow p-3 md:p-6 mx-2 md:mx-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Select Watershed *
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="form-input w-full text-sm md:text-base"
              disabled={farmsLoading}
            >
              <option value="">Choose a watershed...</option>
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} - {farm.country}
                </option>
              ))}
            </select>
            {farmsLoading && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Loading watersheds...</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Select Site *
            </label>
            <select
              value={selectedPlot}
              onChange={(e) => setSelectedPlot(e.target.value)}
              className="form-input w-full text-sm md:text-base"
              disabled={!selectedFarm || plotsLoading}
            >
              <option value="">Choose a site...</option>
              {plots.map((plot) => (
                <option key={plot.id} value={plot.id}>
                  {plot.name}
                </option>
              ))}
            </select>
            {plotsLoading && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Loading sites...</p>
            )}
            {!selectedFarm && (
              <p className="mt-1 text-xs md:text-sm text-secondary-500">Please select a watershed first</p>
            )}
          </div>
        </div>
      </div>

      {/* Filters - Only show when plot is selected */}
      {selectedPlot && (
        <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
          {/* Filter Header */}
          <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
              <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
            </div>
            <button
              onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
            >
              <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
              {isFiltersCollapsed ? (
                <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
              ) : (
                <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
              )}
            </button>
          </div>

          {/* Collapsible Filter Content */}
          {!isFiltersCollapsed && (
            <div className="p-3 md:p-6">
              <div className="space-y-3 md:space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Filter by parameter or sensor type..."
                    value={filters.crop}
                    onChange={(e) => handleFilterChange('crop', e.target.value)}
                    className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                  />
                </div>

                {/* Point ID and Sensor Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Filter by point ID..."
                    value={filters.seed}
                    onChange={(e) => handleFilterChange('seed', e.target.value)}
                    className="form-input w-full text-sm md:text-base"
                  />
                  <input
                    type="text"
                    placeholder="Filter by sensor ID..."
                    value={filters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                    className="form-input w-full text-sm md:text-base"
                  />
                </div>

                <div className="flex justify-center md:justify-end">
                  <button
                    onClick={() => setFilters({ crop: '', seed: '', source: '', page: 1, limit: 20 })}
                    className="btn btn-secondary text-sm md:text-base px-4 py-2"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Points Table - Only show when plot is selected */}
      {selectedPlot ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:flex md:flex-col md:h-[calc(100vh-300px)] md:min-h-[400px]">
            <Table
              columns={columns}
              data={points}
              loading={loading}
              pagination={pagination}
              onPageChange={handlePageChange}
              onSort={handleSort}
              sortConfig={sortConfig}
              emptyMessage="No points found in this plot"
              className="flex-1 flex flex-col"
            />
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 px-2">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : points.length > 0 ? (
              points.map((point) => (
                <div key={point.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3 w-full">
                    <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                      <div className="p-1.5 rounded-md flex-shrink-0 bg-blue-100 text-blue-600">
                        <Layers className="w-3 h-3" />
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="font-medium text-gray-900 text-xs truncate">
                          {point.point_id} - {point.parameter || 'Unknown Parameter'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {point.measurement_units || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <ActionButton
                        icon={Eye}
                        label="View Details"
                        onClick={() => handleViewBed(point)}
                        size="sm"
                        className="p-1"
                      />
                      <ActionMenu
                        item={point}
                        actions={[
                          {
                            key: 'report',
                            label: 'Submit Report',
                            icon: AlertTriangle,
                            variant: 'warning',
                            onClick: handleReportBed,
                          },
                          ...(user && ['admin', 'super_admin', 'watershed_manager'].includes(user.role) ? [
                            {
                              key: 'edit',
                              label: 'Edit Point',
                              icon: Edit,
                              onClick: handleEditBed,
                            },
                            {
                              key: 'delete',
                              label: 'Delete Point',
                              icon: Trash2,
                              variant: 'danger',
                              onClick: handleDeleteBed,
                            },
                          ] : [])
                        ]}
                      />
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs w-full">
                    <div className="min-w-0 overflow-hidden">
                      <div className="text-gray-500 text-xs uppercase tracking-wide">Sensor Type</div>
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {point.sensor_type || 'Not specified'}
                      </div>
                      {point.sensor_id && (
                        <div className="text-xs text-gray-500 truncate">
                          ID: {point.sensor_id}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 overflow-hidden">
                      <div className="text-gray-500 text-xs uppercase tracking-wide">Last Calibration</div>
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {point.calibration_date ? new Date(point.calibration_date).toLocaleDateString() : 'Not set'}
                      </div>
                      {point.qr_code && (
                        <a
                          href={point.qr_code}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          View QR
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
                <Layers className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No points found in this plot</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center mx-2 md:mx-0">
          <Layers className="w-8 h-8 md:w-12 md:h-12 text-secondary-400 mx-auto mb-3 md:mb-4" />
          <h3 className="text-base md:text-lg font-medium text-secondary-900 mb-2">Select Watershed and Site</h3>
          <p className="text-sm md:text-base text-secondary-600">
            Please select a watershed and site above to view and manage monitoring points
          </p>
        </div>
      )}

      {/* View Point Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Point Details"
        size="lg"
      >
        {selectedPoint && (
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Layers className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{selectedPoint.parameter || 'Unknown Parameter'}</h3>
                <p className="text-secondary-600">
                  Point ID: {selectedPoint.point_id} | {selectedPoint.measurement_units || 'N/A'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700">Point ID</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.point_id || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Parameter</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.parameter || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Measurement Units</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.measurement_units || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Sensor Type</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.sensor_type || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Sensor ID</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.sensor_id || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Calibration Date</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {selectedPoint.calibration_date ? new Date(selectedPoint.calibration_date).toLocaleDateString() : 'Not set'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Data Frequency</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.data_frequency || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Power Source</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.power_source || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Communication Type</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.communication_type || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Coordinates</label>
                <p className="mt-1 text-sm text-secondary-900 font-mono">
                  {selectedPoint.coordinates ?
                    `${selectedPoint.coordinates.latitude}, ${selectedPoint.coordinates.longitude}` :
                    'Not specified'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">QR Code</label>
                {selectedPoint.qr_code ? (
                  <a
                    href={selectedPoint.qr_code}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    View QR Code
                  </a>
                ) : (
                  <div className="mt-1">
                    <p className="text-sm text-secondary-500 mb-2">No QR code generated</p>
                    <button
                      onClick={() => handleGenerateQRCode(selectedPoint.id)}
                      disabled={qrCodeGenerating[selectedPoint.id]}
                      className="btn btn-sm btn-primary disabled:opacity-50"
                    >
                      {qrCodeGenerating[selectedPoint.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate QR Code
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedPoint.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Last Updated</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedPoint.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
            {selectedPoint.description && (
              <div>
                <label className="block text-sm font-medium text-secondary-700">Description</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedPoint.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Point Modal */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleFormSubmit}
        title="Edit Point"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Create Point Modal */}
      <FormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleFormSubmit}
        title="Create New Point"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Point"
        message={`Are you sure you want to delete this point with crop "${selectedPoint?.crop}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Submit Report"
        size="xl"
      >
        {reportBedData && (
          <ReportForm
            bedData={reportBedData}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default PointsPage;